import React from 'react';
import { Link } from 'react-router-dom';
import { Typography } from 'antd';
import { PlusCircleTwoTone } from '@ant-design/icons';
import { stripHtml } from '@/utils/helpers';
import { checkPermission } from '@/utils/permission';

const { Text } = Typography;

/**
 * Component hiển thị thông tin khách hàng linh hoạt, cho phép map tên field tuỳ theo module.
 *
 * @param {Object} props
 * @param {Object} props.record - Dữ liệu của hàng hiện tại.
 * @param {string} [props.nameField='customer_name'] - Tên field chứa tên khách hàng.
 * @param {string} [props.idField='customer_id'] - Field chứa ID khách hàng.
 * @param {string} [props.codeField='customer_code'] - Field chứa mã khách hàng.
 * @param {string} [props.branchField='branch_names'] - Field chứa tên chi nhánh.
 * @param {string} [props.medicalBookField='medical_book_number'] - Field chứa mã sổ khám.
 * @param {string} [props.conditionField='conditions'] - Field chứa tình trạng.
 * @param {string} [props.sourceField='customer_source'] - Field chứa nguồn khách hàng.
 * @param {string} [props.tabs] - Tab sẽ mở khi chuyển sang trang chi tiết khách hàng.
 * @param {string} [props.statusField='status'] - Field chứa tình trạng khách hàng.
 * @param {string} [props.statusColorField='status_color'] - Field chúa mẫu tình trạng khách hàng.
 * @param {string} [props.cardNumberField='card_number'] - Field chúa số the khách hàng.
 * @param {boolean} [props.showCardNumber=false] - Có hiển thị số the khách hàng hay không.
 * 
 * @returns {JSX.Element|null}
 */
const CustomerInfoColumn = ({
    record,
    nameField,
    idField,
    codeField,
    branchField,
    medicalBookField,
    conditionField,
    sourceField,
    statusField,
    statusColorField,
    cardNumberField,
    showCardNumber = false,
    tabs
}) => {
    if (!record) return null;

    const name = record?.[nameField];
    const id = record?.[idField];
    const code = record?.[codeField];
    const branch = record?.[branchField];
    const medicalBook = record?.[medicalBookField];
    const source = record?.[sourceField];
    const conditions = record?.[conditionField];
    const status = record?.[statusField];
    const statusColor = record?.[statusColorField];
    const cardNumber = record?.[cardNumberField];

    // build link 1 chỗ duy nhất
    const customerLink = tabs
        ? `/customer/${id}?tabs=${tabs}`
        : `/customer/${id}`;

    const renderName = () => {
        const nameNode = (
            <Text
                strong
                style={{ color: 'inherit' }}
                copyable={{
                    text: name,
                    tooltips: ['Sao chép tên', 'Đã sao chép!'],
                }}
            >
                {name}
            </Text>
        );

        if (id && checkPermission('customer', 'detail')) {
            return (
                <Link
                    to={customerLink}
                    className="color-primary item-action-btn"
                    onClick={(e) => e.stopPropagation()} // tránh click copy bị redirect
                >
                    {nameNode}
                </Link>
            );
        }

        return nameNode;
    };

    return (
        <>
            {name && renderName()}

            {/* Mã khách hàng + chi nhánh */}
            {(code || branch) && (
                <div>
                    {code && (
                        <Text
                            copyable={{
                                text: code,
                                tooltips: ['Sao chép mã', 'Đã sao chép!'],
                            }}
                            style={{
                                color: '#222',
                                fontWeight: 600,
                                background: '#f5f5f5',
                                borderRadius: 4,
                                fontFamily: 'monospace',
                                fontSize: 13,
                            }}
                        >
                            {code}
                        </Text>
                    )}
                    {branch && (
                        <>
                            &nbsp;-{' '}
                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                                {stripHtml(branch)}
                            </span>
                        </>
                    )}
                </div>
            )}

            {/* Mã sổ khám */}
            {medicalBook && (
                <div>
                    <span style={{ fontWeight: 500, fontSize: 11 }}>MSK:</span>&nbsp;
                    <Text
                        copyable={{
                            text: medicalBook,
                            tooltips: ['Sao chép mã', 'Đã sao chép!'],
                        }}
                        style={{
                            color: '#222',
                            fontWeight: 600,
                            background: '#f5f5f5',
                            borderRadius: 4,
                            fontFamily: 'monospace',
                            fontSize: 13,
                        }}
                    >
                        {medicalBook}
                    </Text>
                </div>
            )}

            {/** Mã thẻ */}
            {cardNumber && showCardNumber && (
                <div>
                    <span style={{ fontWeight: 500, fontSize: 11 }}>Mã thẻ:</span>&nbsp;
                    <Text
                        copyable={{
                            text: cardNumber,
                            tooltips: ['Sao chép mã', 'Đã sao chép!'],
                        }}
                        style={{
                            color: '#222',
                            fontWeight: 600,
                            background: '#f5f5f5',
                            borderRadius: 4,
                            fontFamily: 'monospace',
                            fontSize: 13,
                        }}
                    >
                        {cardNumber}
                    </Text>
                </div>
            )}

            {/* Nguồn khách hàng */}
            {source && (
                <div>
                    <span style={{ fontWeight: 500, fontSize: 11 }}>Nguồn:</span>&nbsp;
                    <Text style={{
                        color: '#222',
                        fontWeight: 600,
                        background: '#f5f5f5',
                        borderRadius: 4,
                        fontFamily: 'monospace',
                        wordSpacing: '-3px',
                        fontSize: 13,
                    }}
                    >
                        {source}
                    </Text>
                </div>
            )}

            {/* Trạng thái khách hàng */}
            {status && (
                <div>
                    <span style={{ fontWeight: 500, fontSize: 11 }}>Trạng thái:</span>&nbsp;
                    <Text style={{
                        padding: '0 4px',
                        border: 'none',
                        wordSpacing: '-3px',
                        color: statusColor,
                        fontWeight: 600,
                        background: '#f5f5f5',
                        borderRadius: 4,
                        fontFamily: 'monospace',
                        fontSize: 13,
                    }}
                    >
                        {status}
                    </Text>
                </div>
            )}

            {/* Tình trạng */}
            {conditions && (
                <div style={{ color: 'red' }}>
                    <PlusCircleTwoTone twoToneColor="red" /> &nbsp;
                    {conditions}
                </div>
            )}
        </>
    );
};

export default CustomerInfoColumn;
